/**
 * 项目总览页面
 * Feature: mobile-responsive-framework - 应用响应式框架
 * Feature: real-time-refresh - 实时数据刷新
 * Feature: project-centric-view - 项目为中心的视图
 * Feature: dual-view-mode - 双视图切换（卡片/表格/列表）
 * Feature: project-overview-modal - 项目概览弹窗
 */
import React, { useState, useEffect } from 'react';
import {
  Card, Row, Col, Statistic, Typography, Alert, Space,
  Progress, Tag, Button, Select, Divider, Badge, Modal, message,
  Segmented
} from 'antd';
import {
  ProjectOutlined,
  ClockCircleOutlined,
  ToolOutlined,
  ShoppingOutlined,
  DollarOutlined,
  CheckCircleOutlined,
  SyncOutlined,
  ExclamationCircleOutlined,
  RightOutlined,
  TeamOutlined,
  CalendarOutlined,
  AppstoreOutlined,
  BarsOutlined,
  UnorderedListOutlined
} from '@ant-design/icons';
import { useQuery } from '@tanstack/react-query';
import { useNavigate } from 'react-router-dom';
import dayjs from 'dayjs';
import { StatisticsService } from '../../services/statisticsService';
import { DashboardService, ProjectOverviewData } from '../../services/dashboardService';
import { useResponsiveConfig } from '../../responsive/hooks/useResponsiveConfig';
import { ResponsiveContainer } from '../../responsive/components/ResponsiveContainer';
import { useAutoRefresh } from '../../hooks/useAutoRefresh';
import { useDataChangeDetector } from '../../hooks/useDataChangeDetector';
import { RefreshControl, DataChangeIndicator } from '../../components/RefreshControl/RefreshControl';
import {
  CompactProjectCard,
  ProjectTableView,
  CollapsibleProjectList,
  ProjectSkeleton
} from '../../components/ProjectViews';
import ProjectOverviewModal from '../../components/ProjectOverviewModal';
import { getBudgetBasedColors } from '../../utils/colorUtils';

const { Title, Text } = Typography;
const { Option } = Select;

// 视图模式类型
type ViewMode = 'card' | 'compact' | 'table' | 'list';

// 视图模式配置
const VIEW_MODE_OPTIONS = [
  { value: 'compact', icon: <AppstoreOutlined />, label: '紧凑' },
  { value: 'table', icon: <BarsOutlined />, label: '表格' },
  { value: 'list', icon: <UnorderedListOutlined />, label: '列表' }
];

// 项目状态配置
const PROJECT_STATUS_CONFIG = {
  '进行中': { color: 'processing', icon: <SyncOutlined spin />, text: '进行中' },
  '已完成': { color: 'success', icon: <CheckCircleOutlined />, text: '已完成' },
  '待开始': { color: 'default', icon: <ExclamationCircleOutlined />, text: '待开始' },
  '已暂停': { color: 'warning', icon: <ExclamationCircleOutlined />, text: '已暂停' }
};

// 本地存储键
const VIEW_MODE_STORAGE_KEY = 'dashboard_view_mode';

const Dashboard: React.FC = () => {
  // 路由导航
  const navigate = useNavigate();
  
  // 响应式配置
  const { isMobile, isTablet, gridGutter } = useResponsiveConfig();

  // 视图模式状态（从本地存储读取默认值）
  const [viewMode, setViewMode] = useState<ViewMode>(() => {
    const saved = localStorage.getItem(VIEW_MODE_STORAGE_KEY);
    if (saved && ['card', 'compact', 'table', 'list'].includes(saved)) {
      return saved as ViewMode;
    }
    // 移动端默认使用列表视图，PC端默认使用紧凑卡片
    return isMobile ? 'list' : 'compact';
  });

  // 项目筛选状态
  const [projectFilter, setProjectFilter] = useState<string>('all');
  
  // 项目概览弹窗状态
  const [overviewModalVisible, setOverviewModalVisible] = useState(false);
  const [selectedProject, setSelectedProject] = useState<ProjectOverviewData | null>(null);
  
  // 添加数据弹窗状态
  const [addDataModalVisible, setAddDataModalVisible] = useState(false);
  const [selectedProjectForAdd, setSelectedProjectForAdd] = useState<ProjectOverviewData | null>(null);

  // 保存视图模式到本地存储
  useEffect(() => {
    localStorage.setItem(VIEW_MODE_STORAGE_KEY, viewMode);
  }, [viewMode]);

  // 移动端自动切换到列表视图
  useEffect(() => {
    if (isMobile && viewMode === 'table') {
      setViewMode('list');
    }
  }, [isMobile]);

  // 点击项目卡片 - 打开概览弹窗
  const handleProjectClick = (project: ProjectOverviewData) => {
    setSelectedProject(project);
    setOverviewModalVisible(true);
  };

  // 关闭概览弹窗
  const handleCloseOverviewModal = () => {
    setOverviewModalVisible(false);
    setSelectedProject(null);
  };

  // 导出项目数据
  const handleExport = async (project: ProjectOverviewData) => {
    try {
      message.loading({ content: '正在导出...', key: 'export' });
      navigate(`/reports/project-comprehensive?projectId=${project.id}&action=export`);
      message.success({ content: '已跳转到报表页面，请在报表页面进行导出', key: 'export' });
    } catch (error) {
      message.error({ content: '导出失败', key: 'export' });
    }
  };

  // 添加数据
  const handleAddData = (project: ProjectOverviewData) => {
    setSelectedProjectForAdd(project);
    setAddDataModalVisible(true);
  };

  // 添加数据选项
  const handleAddDataOption = (type: 'worktime' | 'equipment' | 'material') => {
    setAddDataModalVisible(false);
    if (!selectedProjectForAdd) return;
    
    switch (type) {
      case 'worktime':
        navigate(`/worktime?projectId=${selectedProjectForAdd.id}`);
        break;
      case 'equipment':
        navigate(`/equipment?projectId=${selectedProjectForAdd.id}`);
        break;
      case 'material':
        navigate(`/material?projectId=${selectedProjectForAdd.id}`);
        break;
    }
  };

  // 获取Dashboard完整数据（使用聚合接口）
  const { 
    data: dashboardData, 
    isLoading: dashboardLoading, 
    error: dashboardError, 
    refetch: refetchDashboard 
  } = useQuery({
    queryKey: ['dashboard', 'overview'],
    queryFn: () => DashboardService.getDashboardData(),
    staleTime: 0, // 每次都重新请求，不使用缓存
    gcTime: 0,
    refetchInterval: false,
    refetchOnWindowFocus: false,
  });

  // 根据筛选条件过滤项目
  const filteredProjects = React.useMemo(() => {
    if (!dashboardData?.projects) return [];
    if (projectFilter === 'all') return dashboardData.projects;
    return dashboardData.projects.filter(p => p.status === projectFilter);
  }, [dashboardData?.projects, projectFilter]);

  // 自动刷新控制（带冷却时间）
  const refreshState = useAutoRefresh({
    enabled: false, // 默认关闭自动刷新
    interval: 60000, // 60秒自动刷新
    manualRefreshCooldown: 10000, // 10秒手动刷新冷却
    onRefresh: async () => {
      await refetchDashboard();
    }
  });

  // 数据变化检测
  const overviewChangeDetector = useDataChangeDetector({
    data: dashboardData?.overview,
    enabled: true
  });

  if (dashboardError) {
    return (
      <ResponsiveContainer maxWidth="full" padding="none">
        <Title level={isMobile ? 4 : 2}>项目总览</Title>
        <Alert
          message="数据加载失败"
          description="无法连接到后端服务，请检查网络连接或联系管理员"
          type="warning"
          showIcon
          style={{ marginBottom: 16 }}
        />
      </ResponsiveContainer>
    );
  }

  // 渲染紧凑卡片视图
  const renderCompactCardView = () => (
    <Row gutter={[12, 12]}>
      {filteredProjects.map((project) => (
        <Col xs={12} sm={8} md={6} lg={6} xl={4} key={project.id}>
          <CompactProjectCard
            project={project}
            onClick={handleProjectClick}
            isMobile={isMobile}
          />
        </Col>
      ))}
    </Row>
  );

  // 渲染完整卡片视图
  const renderFullCardView = () => (
    <Row gutter={[gridGutter, gridGutter]}>
      {filteredProjects.map((project) => {
        const health = DashboardService.calculateProjectHealth(project.budget, project.actualCost);
        const statusConfig = PROJECT_STATUS_CONFIG[project.status as keyof typeof PROJECT_STATUS_CONFIG] || PROJECT_STATUS_CONFIG['待开始'];
        const budgetColors = getBudgetBasedColors(project.budget, project.actualCost);

        return (
          <Col xs={24} sm={24} md={12} lg={8} key={project.id}>
            <Card
              hoverable
              size={isMobile ? 'small' : 'default'}
              style={{ 
                height: '100%',
                backgroundColor: budgetColors.backgroundColor,
                borderColor: budgetColors.borderColor,
                transition: 'all 0.3s ease'
              }}
              bodyStyle={{ padding: isMobile ? 12 : 24 }}
              onClick={() => handleProjectClick(project)}
            >
              {/* 项目标题和状态 */}
              <div style={{ marginBottom: 16 }}>
                <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', marginBottom: 8 }}>
                  <Title level={isMobile ? 5 : 4} style={{ margin: 0, flex: 1 }}>
                    {project.name}
                  </Title>
                  <Tag color={statusConfig.color} icon={statusConfig.icon}>
                    {statusConfig.text}
                  </Tag>
                </div>
                <Text type="secondary" style={{ fontSize: isMobile ? 12 : 14 }}>
                  {project.recentActivity}
                </Text>
              </div>

              <Divider style={{ margin: '12px 0' }} />

              {/* 预算和成本 */}
              <div style={{ marginBottom: 16 }}>
                <div style={{ display: 'flex', justifyContent: 'space-between', marginBottom: 8 }}>
                  <Text strong>预算使用</Text>
                  <Badge
                    status={health.color === '#52c41a' ? 'success' : health.color === '#f5222d' ? 'error' : 'warning'}
                    text={health.status}
                  />
                </div>
                <Progress
                  percent={Math.min(health.percentage, 100)}
                  strokeColor={health.color}
                  size={isMobile ? 'small' : 'default'}
                  format={() => `${health.percentage.toFixed(1)}%`}
                />
                <div style={{ display: 'flex', justifyContent: 'space-between', marginTop: 8 }}>
                  <Text type="secondary" style={{ fontSize: 12 }}>
                    实际: ¥{project.actualCost.toLocaleString()}
                  </Text>
                  <Text type="secondary" style={{ fontSize: 12 }}>
                    预算: ¥{project.budget.toLocaleString()}
                  </Text>
                </div>
              </div>

              {/* 项目进度 */}
              <div style={{ marginBottom: 16 }}>
                <div style={{ display: 'flex', justifyContent: 'space-between', marginBottom: 8 }}>
                  <Text strong>项目进度</Text>
                  <Text>{project.progress}%</Text>
                </div>
                <Progress
                  percent={project.progress}
                  strokeColor="#1677ff"
                  size={isMobile ? 'small' : 'default'}
                />
              </div>

              {/* 项目信息 */}
              <Row gutter={[8, 8]}>
                <Col span={12}>
                  <Space size={4}>
                    <TeamOutlined style={{ color: '#1677ff' }} />
                    <Text type="secondary" style={{ fontSize: 12 }}>
                      {project.teamSize} 条记录
                    </Text>
                  </Space>
                </Col>
                <Col span={12}>
                  <Space size={4}>
                    <CalendarOutlined style={{ color: '#52c41a' }} />
                    <Text type="secondary" style={{ fontSize: 12 }}>
                      {project.daysRemaining > 0 ? `剩余 ${project.daysRemaining} 天` : '已完成'}
                    </Text>
                  </Space>
                </Col>
              </Row>

              <Divider style={{ margin: '12px 0' }} />

              {/* 操作按钮 */}
              <div style={{ display: 'flex', justifyContent: 'space-between' }}>
                <Button 
                  type="link" 
                  size="small" 
                  style={{ padding: 0 }}
                  onClick={(e) => {
                    e.stopPropagation();
                    handleProjectClick(project);
                  }}
                >
                  查看详情 <RightOutlined />
                </Button>
                <Space size={8}>
                  <Button 
                    type="text" 
                    size="small"
                    onClick={(e) => {
                      e.stopPropagation();
                      handleExport(project);
                    }}
                  >
                    导出
                  </Button>
                  <Button 
                    type="text" 
                    size="small"
                    onClick={(e) => {
                      e.stopPropagation();
                      handleAddData(project);
                    }}
                  >
                    添加数据
                  </Button>
                </Space>
              </div>
            </Card>
          </Col>
        );
      })}
    </Row>
  );

  // 渲染表格视图
  const renderTableView = () => (
    <ProjectTableView
      projects={filteredProjects}
      loading={dashboardLoading}
      onViewDetails={handleProjectClick}
      onExport={handleExport}
      onAddData={handleAddData}
      isMobile={isMobile}
    />
  );

  // 渲染列表视图
  const renderListView = () => (
    <CollapsibleProjectList
      projects={filteredProjects}
      onViewDetails={handleProjectClick}
      onExport={handleExport}
      onAddData={handleAddData}
    />
  );

  // 根据视图模式渲染项目列表
  const renderProjectList = () => {
    if (dashboardLoading) {
      return <ProjectSkeleton count={isMobile ? 4 : 8} isMobile={isMobile} viewMode={viewMode} />;
    }

    if (!filteredProjects || filteredProjects.length === 0) {
      return (
        <Card>
          <div style={{ textAlign: 'center', padding: '40px 0' }}>
            <ProjectOutlined style={{ fontSize: 64, color: '#d9d9d9', marginBottom: 16 }} />
            <Title level={4} type="secondary">暂无项目</Title>
            <Text type="secondary">
              {projectFilter === 'all' ? '还没有创建任何项目' : `没有${projectFilter}的项目`}
            </Text>
            <div style={{ marginTop: 16 }}>
              <Button type="primary" onClick={() => navigate('/project')}>创建新项目</Button>
            </div>
          </div>
        </Card>
      );
    }

    switch (viewMode) {
      case 'compact':
        return renderCompactCardView();
      case 'card':
        return renderFullCardView();
      case 'table':
        return renderTableView();
      case 'list':
        return renderListView();
      default:
        return renderCompactCardView();
    }
  };

  return (
    <ResponsiveContainer maxWidth="full" padding="none">
      <Space orientation="vertical" size="large" style={{ width: '100%' }}>
        {/* 页面标题和刷新控制 */}
        <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', flexWrap: 'wrap', gap: 16 }}>
          <Title level={isMobile ? 4 : 2} style={{ margin: 0 }}>
            <ProjectOutlined /> 项目总览
          </Title>
          <RefreshControl
            refreshState={refreshState}
            showAutoRefresh={!isMobile}
            showLastRefreshTime={true}
            size={isMobile ? 'small' : 'middle'}
          />
        </div>

        {/* 数据变化提示 */}
        {overviewChangeDetector.hasChanges && (
          <DataChangeIndicator
            hasChanges={overviewChangeDetector.hasChanges}
            onViewChanges={() => {
              overviewChangeDetector.acknowledgeChanges();
            }}
          />
        )}

        {/* 总体统计卡片 */}
        {dashboardLoading ? (
          <Row gutter={[gridGutter, gridGutter]}>
            {[1, 2, 3, 4].map((i) => (
              <Col xs={12} sm={6} md={6} key={i}>
                <Card size={isMobile ? 'small' : 'default'} loading />
              </Col>
            ))}
          </Row>
        ) : (
          <Row gutter={[gridGutter, gridGutter]}>
            <Col xs={12} sm={6} md={6}>
              <Card size={isMobile ? 'small' : 'default'} variant="borderless" style={{ background: '#f0f5ff' }}>
                <Statistic
                  title="项目总数"
                  value={dashboardData?.overview?.totalProjects || 0}
                  prefix={<ProjectOutlined style={{ color: '#1677ff' }} />}
                  valueStyle={{ color: '#1677ff', fontSize: isMobile ? 20 : 28 }}
                />
              </Card>
            </Col>
            <Col xs={12} sm={6} md={6}>
              <Card size={isMobile ? 'small' : 'default'} variant="borderless" style={{ background: '#f6ffed' }}>
                <Statistic
                  title="活跃项目"
                  value={dashboardData?.overview?.activeProjects || 0}
                  prefix={<SyncOutlined style={{ color: '#52c41a' }} />}
                  valueStyle={{ color: '#52c41a', fontSize: isMobile ? 20 : 28 }}
                />
              </Card>
            </Col>
            <Col xs={12} sm={6} md={6}>
              <Card size={isMobile ? 'small' : 'default'} variant="borderless" style={{ background: '#fff7e6' }}>
                <Statistic
                  title="总成本"
                  value={dashboardData?.overview?.totalCost || 0}
                  prefix={<DollarOutlined style={{ color: '#fa8c16' }} />}
                  precision={isMobile ? 0 : 0}
                  formatter={(value) => `¥${(Number(value) / 10000).toFixed(1)}万`}
                  valueStyle={{ color: '#fa8c16', fontSize: isMobile ? 20 : 28 }}
                />
              </Card>
            </Col>
            <Col xs={12} sm={6} md={6}>
              <Card size={isMobile ? 'small' : 'default'} variant="borderless" style={{ background: '#e6f7ff' }}>
                <Statistic
                  title="总工时"
                  value={dashboardData?.overview?.totalWorkHours || 0}
                  prefix={<ClockCircleOutlined style={{ color: '#13c2c2' }} />}
                  suffix={isMobile ? 'h' : '小时'}
                  valueStyle={{ color: '#13c2c2', fontSize: isMobile ? 20 : 28 }}
                />
              </Card>
            </Col>
          </Row>
        )}

        {/* 项目筛选和视图切换 */}
        <Card size={isMobile ? 'small' : 'default'}>
          <div style={{ 
            display: 'flex', 
            justifyContent: 'space-between', 
            alignItems: 'center', 
            flexWrap: 'wrap', 
            gap: 12 
          }}>
            <Space size={12} wrap>
              <Text strong style={{ fontSize: isMobile ? 14 : 16 }}>项目列表</Text>
              <Select
                value={projectFilter}
                onChange={setProjectFilter}
                style={{ width: isMobile ? 100 : 120 }}
                size={isMobile ? 'small' : 'middle'}
              >
                <Option value="all">全部项目</Option>
                <Option value="进行中">进行中</Option>
                <Option value="已完成">已完成</Option>
                <Option value="待开始">待开始</Option>
                <Option value="已暂停">已暂停</Option>
              </Select>
            </Space>
            
            <Space size={12} wrap>
              {/* 视图切换 */}
              <Segmented
                value={viewMode}
                onChange={(value) => setViewMode(value as ViewMode)}
                options={VIEW_MODE_OPTIONS.filter(opt => 
                  // 移动端隐藏表格视图选项
                  !(isMobile && opt.value === 'table')
                ).map(opt => ({
                  value: opt.value,
                  icon: opt.icon,
                  title: opt.label
                }))}
                size={isMobile ? 'small' : 'middle'}
              />
              
              <Text type="secondary" style={{ fontSize: 12 }}>
                共 {filteredProjects.length} 个项目
              </Text>
            </Space>
          </div>
        </Card>

        {/* 项目列表 */}
        {renderProjectList()}
      </Space>

      {/* 项目概览弹窗 */}
      <ProjectOverviewModal
        visible={overviewModalVisible}
        project={selectedProject}
        onClose={handleCloseOverviewModal}
        onExport={handleExport}
      />

      {/* 添加数据弹窗 */}
      <Modal
        title={`添加数据 - ${selectedProjectForAdd?.name || ''}`}
        open={addDataModalVisible}
        onCancel={() => setAddDataModalVisible(false)}
        footer={null}
        width={400}
      >
        <div style={{ padding: '16px 0' }}>
          <Space orientation="vertical" style={{ width: '100%' }} size="middle">
            <Button 
              block 
              size="large"
              icon={<ClockCircleOutlined />}
              onClick={() => handleAddDataOption('worktime')}
            >
              添加工时记录
            </Button>
            <Button 
              block 
              size="large"
              icon={<ToolOutlined />}
              onClick={() => handleAddDataOption('equipment')}
            >
              添加设备使用记录
            </Button>
            <Button 
              block 
              size="large"
              icon={<ShoppingOutlined />}
              onClick={() => handleAddDataOption('material')}
            >
              添加材料使用记录
            </Button>
          </Space>
        </div>
      </Modal>
    </ResponsiveContainer>
  );
};

export default Dashboard;
